import { Input, Collapse, Select, Switch, Badge, Space, Button, Tooltip } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilterSlash } from '@fortawesome/pro-regular-svg-icons'
import { ScheduleCreateEventFilters, countActiveFilters, defaultScheduleCreateEventFilters } from './scheduleCreateEventModel'
import { useSchedule } from '../ScheduleContext'
import { projectTypeOptionsFor } from '../../../common/constants'
import useServiceTicketsEnabled from '../../../hooks/useServiceTicketsEnabled'

type Props = {
  filters: ScheduleCreateEventFilters
  setFilters: (filters: ScheduleCreateEventFilters) => void
}

const JobSearchFilters = ({ filters, setFilters }: Props) => {
  const { crew, divisions } = useSchedule()
  const showServiceTickets = useServiceTicketsEnabled()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const crewOptions = crew?.map(c => ({ value: c.id, label: c.name }))
  const uniqueDivisionNames = [...new Set(divisions?.map(d => d.name) || [])].sort((a, b) => a.localeCompare(b))
  const divisionsOptions = uniqueDivisionNames.map(name => ({ value: name, label: name }))
  const selectedDivisionNames = [...new Set(
    filters.divisionIds
      .map(id => divisions?.find(d => d.id === id)?.name)
      .filter((name): name is string => name != null)
  )]
  const activeFilterCount = countActiveFilters(filters)

  return (
    <>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <Input.Search
          placeholder="Search by job or client"
          size="large"
          style={{ flex: 1 }}
          value={filters.searchQuery}
          onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
        />
        <Tooltip title="Clear filters">
          <Button
            size="large"
            icon={<FontAwesomeIcon icon={faFilterSlash} />}
            onClick={() => setFilters(defaultScheduleCreateEventFilters)}
            disabled={activeFilterCount === 0 && !filters.searchQuery}
          />
        </Tooltip>
      </div>

      <Collapse
        style={{ backgroundColor: 'white', borderRadius: '8px' }}
        items={[
          {
            key: '1',
            label: <Space><span>Filter By</span><Badge count={activeFilterCount} /></Space>,
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div>
                  <div style={{ marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#333' }}>
                    Division
                  </div>
                  <Select
                    mode="multiple"
                    maxTagCount={2}
                    style={{ width: '100%', height: '48px' }}
                    placeholder="Select Divisions"
                    options={divisionsOptions}
                    value={selectedDivisionNames}
                    onChange={(names: string[]) => {
                      const ids = divisions?.filter(d => names.includes(d.name)).map(d => d.id) || []
                      setFilters({ ...filters, divisionIds: ids })
                    }}
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                </div>

                <div>
                  <div style={{ marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#333' }}>
                    Crew Member
                  </div>
                  <Select
                    mode="multiple"
                    maxTagCount={2}
                    style={{ width: '100%', height: '48px' }}
                    suffixIcon={<DownOutlined />}
                    placeholder="Select Crew Members"
                    options={crewOptions}
                    value={filters.crewMemberIds}
                    onChange={(value) => setFilters({ ...filters, crewMemberIds: value })}
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                </div>

                <div>
                  <div style={{ marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#333' }}>
                    Job Type
                  </div>
                  <Select
                    mode="multiple"
                    maxTagCount={2}
                    style={{ width: '100%', height: '48px' }}
                    suffixIcon={<DownOutlined />}
                    placeholder="Select Job Types"
                    options={projectTypeOptions}
                    value={filters.projectTypeIds}
                    onChange={(value) => setFilters({ ...filters, projectTypeIds: value })}
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Switch
                    checked={filters.showCompleteInactiveJobs}
                    onChange={(checked) => setFilters({ ...filters, showCompleteInactiveJobs: checked })}
                  />
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    Include Complete or Inactive Jobs
                  </span>
                </div>
              </div>
            )
          }
        ]}
      />
    </>
  )
}

export default JobSearchFilters
